import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MultiSelect } from '@/components/ui/multi-select';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { EmployeeStatus } from '@/contexts/employees/EmployeesContext';
import { SystemUser } from '@/pages/user/hooks/useUserManagement';
import { UserRole } from '@/types';
import { UserRole as UserRoleEnum } from '@/types/enums';

const statuses = [
  EmployeeStatus.ACTIVE,
  EmployeeStatus.INACTIVE,
  EmployeeStatus.SUSPENDED
];

const roles = [
  { value: UserRoleEnum.DEPARTMENT_HEAD, label: 'Department Head' },
  { value: UserRoleEnum.PLANT_MANAGER, label: 'Plant Manager' },
  { value: UserRoleEnum.HR, label: 'HR' },
  { value: UserRoleEnum.SUPER_ADMIN, label: 'Super Admin' }
];

interface Department {
  id: string;
  name: string;
}

interface EditUserFormProps {
  user: SystemUser
  onSave: (updatedUser: SystemUser) => void
  onCancel: () => void
}

export function EditUserForm({ user, onSave, onCancel }: EditUserFormProps) {
  const [formData, setFormData] = useState({
    name: user.name,
    email: user.email,
    position: user.position,
    department: Array.isArray(user.department) ? user.department : (user.department ? [user.department] : []),
    status: user.status || "active",
    role: user.role,
  });
  const [departments, setDepartments] = useState<Department[]>([]);
  const [isLoadingDepartments, setIsLoadingDepartments] = useState(false);

  // Re-initialize form data when user prop changes
  useEffect(() => {
    setFormData({
      name: user.name,
      email: user.email,
      position: user.position,
      department: Array.isArray(user.department) ? user.department : (user.department ? [user.department] : []),
      status: user.status || "active",
      role: user.role,
    });
  }, [user]);

  useEffect(() => {
    const fetchDepartments = async () => {
      setIsLoadingDepartments(true);
      try {
        const { data, error } = await supabase
          .from('departments')
          .select('id, name')
          .order('name');
          
        if (error) throw error;
        const fetchedDepartments = data || [];
        setDepartments(fetchedDepartments);
      } catch {
        toast.error('Failed to load departments');
      } finally {
        setIsLoadingDepartments(false);
      }
    };
    fetchDepartments();
  }, [user.department]);

  // Re-initialize form data when departments are loaded and user is a department head
  useEffect(() => {
    if (departments.length > 0 && user.role === UserRoleEnum.DEPARTMENT_HEAD) {
      const userDepartments = Array.isArray(user.department) 
        ? user.department 
        : (user.department ? [user.department] : []);
      
      setFormData(prev => ({
        ...prev,
        department: userDepartments
      }));
    }
  }, [departments, user.department, user.role]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    if (name in formData) {
        setFormData({
            ...formData,
            [name]: value
        })
    }
  }

  const handleSelectChange = (name: keyof typeof formData, value: string) => {
    if (typeof value === 'string') {
      setFormData(prev => {
        const newData = {
          ...prev,
          [name]: value
        };
        
        // If role is changed to something other than department_head, clear department
        if (name === 'role' && value !== UserRoleEnum.DEPARTMENT_HEAD) {
          newData.department = [];
        }
        
        return newData;
      });
    }
  }

  const handleDepartmentChange = (selectedDepartments: string[]) => {
    setFormData(prev => ({
      ...prev,
      department: selectedDepartments
    }));
  }

  const handleSubmit = () => {
    // Convert department names to department names (keep as array for multi-select)
    const updatedUser: SystemUser = {
      ...user,
      name: formData.name,
      email: formData.email,
      position: formData.position,
      department: formData.department, // Already an array of department names
      status: formData.status,
      role: formData.role as UserRole,
    };

    onSave(updatedUser);
  }

  return (
    <div className="space-y-6">
      
      {/* Avatar and basic info section */}
      <div className="flex items-center space-x-4 p-4 border rounded-lg">
        <Avatar className="h-16 w-16">
          <AvatarFallback className="text-xl">
            {formData.name.charAt(0).toUpperCase()}
          </AvatarFallback>
        </Avatar>
        <div>
          <h3 className="font-semibold">{formData.name}</h3>
          <p className="text-muted-foreground">{formData.email}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="name">Full Name</Label>
          <Input
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            placeholder="Enter full name"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleInputChange}
            placeholder="Enter email address"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="position">Position</Label>
          <Input
            id="position"
            name="position"
            value={formData.position}
            onChange={handleInputChange}
            placeholder="Enter position"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="role">Role</Label>
          <Select 
            value={formData.role} 
            onValueChange={(value) => handleSelectChange('role', value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select role" />
            </SelectTrigger>
            <SelectContent>
              {roles.map((role) => (
                <SelectItem key={role.value} value={role.value}>
                  {role.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {formData.role === UserRoleEnum.DEPARTMENT_HEAD && (
          <div className="space-y-2">
            <Label htmlFor="department">Departments</Label>
            <MultiSelect
              options={departments.map(dept => ({ label: dept.name, value: dept.name }))}
              selected={formData.department}
              onChange={handleDepartmentChange}
              placeholder={isLoadingDepartments ? "Loading..." : "Select departments..."}
              disabled={isLoadingDepartments}
              maxDisplay={2}
            />
          </div>
        )}

        {formData.role !== UserRoleEnum.DEPARTMENT_HEAD && (
          <div className="space-y-2">
            <Label>Department Access</Label>
            <div className="flex items-center h-10 px-3 py-2 border border-input bg-muted rounded-md text-sm text-muted-foreground">
              All Departments
            </div>
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="status">Status</Label>
          <Select 
            value={formData.status} 
            onValueChange={(value) => handleSelectChange('status', value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select status" />
            </SelectTrigger>
            <SelectContent>
              {statuses.map((status) => (
                <SelectItem key={status} value={status}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex justify-end gap-2 mt-6">
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button onClick={handleSubmit}>Save Changes</Button>
      </div>
    </div>
  )
} 